/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './ResourceBank.css';

// --- SVG Icons ---
const Icons = {
    Upload: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>,
    File: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>,
    Download: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Filter: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
    Folder: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
};

const ResourceBank = () => {
    const [resources, setResources] = useState([]);
    const [userRole, setUserRole] = useState('');
    const [filters, setFilters] = useState({ grade: '', subject: '' });
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [expandedSubject, setExpandedSubject] = useState(null);

    // Form Refs
    const titleRef = useRef();
    const gradeRef = useRef();
    const subjectRef = useRef();
    const fileRef = useRef();

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

    useEffect(() => {
        const role = localStorage.getItem('user_role') || '';
        setUserRole(role);
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get(`${API_URL}/fetch_library.php`, {
                params: filters,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setResources(res.data.resources || []);
        } catch (err) {
            setResources([]);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchResources();
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setUploadError('');

        const formData = new FormData();
        formData.append('title', titleRef.current.value);
        formData.append('grade_level', gradeRef.current.value);
        formData.append('subject', subjectRef.current.value);
        formData.append('file', fileRef.current.files[0]);

        try {
            const token = localStorage.getItem('auth_token');
            await axios.post(`${API_URL}/admin_upload_resource.php`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                },
            });
            alert("Upload Successful");
            fetchResources();
            e.target.reset();
        } catch (err) {
            setUploadError(err.response?.data?.error || 'Upload failed');
        }
        setUploading(false);
    };

    const handleDeleteResource = async (resId) => {
        if (!window.confirm('Are you sure you want to delete this resource?')) return;
        try {
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('id', resId);
            await axios.post(`${API_URL}/delete_resource.php`, formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchResources();
        } catch (err) {
            alert('Delete failed: ' + (err.response?.data?.error || err.message));
        }
    };

    return (
        <div className="resource-wrapper">

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>Resource Library</h1>
                    <p>Access study materials and past papers</p>
                </div>
            </div>

            {/* Admin Upload Section */}
            {userRole === 'developer' && (
                <div className="upload-card">
                    <div className="card-header-row">
                        <h3><Icons.Upload /> Admin Upload</h3>
                    </div>
                    <form onSubmit={handleUpload} className="upload-form">
                        <div className="form-group">
                            <label>Resource Title</label>
                            <input ref={titleRef} type="text" placeholder="e.g. 2024 Math Exam" required className="modern-input" />
                        </div>
                        <div className="form-group half">
                            <label>Grade</label>
                            <input ref={gradeRef} type="number" placeholder="1-12" required className="modern-input" />
                        </div>
                        <div className="form-group half">
                            <label>Subject</label>
                            <select ref={subjectRef} required defaultValue="" className="modern-select">
                                <option value="" disabled>Select Subject</option>
                                <option value="Accounting">Accounting</option>
                                <option value="Afrikaans Home Language">Afrikaans Home Language</option>
                                <option value="Afrikaans FAL">Afrikaans First Additional</option>
                                <option value="Business Studies">Business Studies</option>
                                <option value="Creative Arts">Creative Arts</option>
                                <option value="Computer Applications Technology">CAT</option>
                                <option value="Coding & Robotics">Coding & Robotics</option>
                                <option value="Economics">Economics</option>
                                <option value="EGD">EGD</option>
                                <option value="English HL">English Home Language</option>
                                <option value="English FAL">English First Additional</option>
                                <option value="Geography">Geography</option>
                                <option value="History">History</option>
                                <option value="Mathematics">Mathematics</option>
                                <option value="Physical Sciences">Physical Sciences</option>
                                <option value="Life Skills">Life Skills</option>
                                <option value="Life Sciences">Life Sciences</option>
                                <option value="Natural Sciences">Natural Sciences</option>
                                <option value="NST">NST (Natural Sciences and Technology)</option>
                                <option value="Information Technology">IT</option>
                                <option value="Technology">Technology</option>
                                <option value="Technical Mathematics">Technical Mathematics</option>
                                <option value="Technical Sciences">Technical Sciences</option>
                                <option value="Tourism">Tourism</option>
                                <option value="Life Orientation">Life Orientation</option>
                            </select>
                        </div>
                        <div className="form-group full">
                            <label>File Attachment</label>
                            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.pptx" required className="file-input" />
                        </div>
                        <button type="submit" disabled={uploading} className="btn-primary full-width">
                            {uploading ? 'Uploading...' : 'Upload Resource'}
                        </button>
                    </form>
                    {uploadError && <div className="error-message">{uploadError}</div>}
                </div >
            )}

            {/* Filter Bar */}
            <div className="filter-bar">
                <form onSubmit={handleFilterSubmit} className="filter-form">
                    <div className="filter-group">
                        <select
                            name="grade"
                            value={filters.grade}
                            onChange={handleFilterChange}
                            className="modern-select small"
                        >
                            <option value="">All Grades</option>
                            {Array.from({length:12}, (_,i) => i+1).map(g => (
                                <option key={g} value={g}>Grade {g}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <select
                            name="subject"
                            value={filters.subject}
                            onChange={handleFilterChange}
                            className="modern-select small"
                        >
                            <option value="">All Subjects</option>
                            {Array.from(new Set(resources.map(r => r.subject))).sort().map(subject => (
                                <option key={subject} value={subject}>{subject}</option>
                            ))}
                        </select>
                    </div>
                    <button type="submit" className="btn-filter">
                        <Icons.Filter /> Filter
                    </button>
                </form>
                <span className="resource-count">{resources.length} Files Found</span>
            </div>

            {/* Resources Container */}
            <div className="resources-content">
                {resources.length === 0 ? (
                    <div className="empty-state">
                        <p>No resources found matching your criteria.</p>
                    </div>
                ) : (
                    <div className="resources-container">
                        {Object.entries(
                            resources.reduce((groups, res) => {
                                const key = res.subject;
                                if (!groups[key]) groups[key] = [];
                                groups[key].push(res);
                                return groups;
                            }, {})
                        ).sort((a, b) => a[0].localeCompare(b[0])).map(([subject, items]) => {
                            const byGrade = items.reduce((groups, res) => {
                                const grade = res.grade_level;
                                if (!groups[grade]) groups[grade] = [];
                                groups[grade].push(res);
                                return groups;
                            }, {});

                            return (
                                <div key={subject} className="subject-section">
                                    <div 
                                        className="section-header" 
                                        onClick={() => setExpandedSubject(expandedSubject === subject ? null : subject)}
                                    >
                                        <div className="subject-title-group">
                                            <Icons.Folder />
                                            <h2>{subject}</h2>
                                            <span className="item-count">{items.length}</span>
                                        </div>
                                        <span className={`expand-icon ${expandedSubject === subject ? 'open' : ''}`}>▼</span>
                                    </div>

                                    {expandedSubject === subject && (
                                        <div className="section-content">
                                            {Object.entries(byGrade).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([grade, files]) => (
                                                <div key={grade} className="grade-subsection">
                                                    <div className="grade-header">
                                                        <h4>Grade {grade}</h4>
                                                        <span className="file-count">{files.length} file{files.length !== 1 ? 's' : ''}</span>
                                                    </div>
                                                    <div className="files-grid">
                                                        {files.map((res) => (
                                                            <div key={res.id} className="resource-card">
                                                                <div className="card-header">
                                                                    <Icons.File />
                                                                    <span className="file-date">{new Date(res.uploaded_at).toLocaleDateString()}</span>
                                                                </div>
                                                                <div className="card-body">
                                                                    <h5 title={res.title}>{res.title}</h5>
                                                                </div>
                                                                <div className="card-footer">
                                                                    {res.file_path && (
                                                                        <a
                                                                            href={`https://marksure.tech/${res.file_path}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn-download"
                                                                        >
                                                                            <Icons.Download /> Download
                                                                        </a>
                                                                    )}
                                                                    {userRole === 'developer' && (
                                                                        <button
                                                                            onClick={() => handleDeleteResource(res.id)}
                                                                            className="btn-delete"
                                                                            title="Delete"
                                                                        >
                                                                            <Icons.Trash />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div >
    );
};

export default ResourceBank;
